import React, { createContext, FunctionComponent, PropsWithChildren, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { StarredMenuQuery } from './__generated__/StarredMenuQuery.graphql';
import { StarredMenuSaveMutation } from './__generated__/StarredMenuSaveMutation.graphql';
import type { ItemMenu } from './Menu';
import type { QuickAccessEnvironment } from './leftAppSidebarTypes';

// ── Starred pages ───────────────────────────────────────────────────
// The user can "star" any leaf page, either from the sidebar (star toggle on a
// menu item) or from a page header (star next to the tool name). Starred pages
// are surfaced in the sidebar right after Orders, so they are reachable without
// drilling into categories.
//
// A single provider holds the starred keys for the whole app so the sidebar and
// every page header stay in sync. The keys are persisted server-side via the
// same backend field that used to power the (now removed) Quick Access section —
// hence the reused `quickAccessMenu` / `saveQuickAccessMenu` GraphQL operations.

// Stable identity used to persist a starred page. Matches the menu item `key`
// used by the backend tree menu (and therefore the `pageKey` of page headers).
export const getStarredKey = (item: ItemMenu) => item.key || item.urlPage;

export const isStarredKeyMatch = (item: ItemMenu, key: string) => getStarredKey(item) === key || item.urlPage === key;

// Pages that are always present in the menu as fixed entries (Customizable
// Products, Printing Methods, Orders) and therefore must not be star-toggleable.
const STARRED_EXCLUDED_KEYS = new Set(['products', 'printing-methods', 'orders']);

export const isStarExcludedKey = (key: string) => STARRED_EXCLUDED_KEYS.has(key);

interface StarredMenuContextValue {
    // Whether a provider is mounted above. Lets consumers outside the app shell
    // (e.g. a page header rendered without the provider) hide their star UI.
    isWithinProvider: boolean;
    starredKeys: string[];
    isLoaded: boolean;
    isStarred: (item: ItemMenu) => boolean;
    isStarredKey: (key: string) => boolean;
    toggleStar: (item: ItemMenu) => void;
    toggleStarKey: (key: string) => void;
    saveStarredKeysOrder: (nextKeys: string[]) => void;
    isSaving: boolean;
}

const StarredMenuContext = createContext<StarredMenuContextValue>({
    isWithinProvider: false,
    starredKeys: [],
    isLoaded: false,
    isStarred: () => false,
    isStarredKey: () => false,
    toggleStar: () => {},
    toggleStarKey: () => {},
    saveStarredKeysOrder: () => {},
    isSaving: false
});

export const useStarredMenu = () => useContext(StarredMenuContext);

// Backed by the same server fields that previously powered Quick Access
// (only the client operation names differ).
const starredKeysQuery = graphql`
    query StarredMenuQuery($environment: ZakekeEnvironment!) {
        quickAccessMenu(environment: $environment) {
            keys
        }
    }
`;

const saveStarredKeysMutation = graphql`
    mutation StarredMenuSaveMutation($input: QuickAccessMenuInput!) {
        saveQuickAccessMenu(input: $input) {
            keys
        }
    }
`;

// Loads the persisted starred keys and pushes them into the provider. Kept in
// its own Suspense boundary so the app renders immediately while the (tiny)
// starred keys query resolves in the background.
const StarredKeysLoader: FunctionComponent<{
    environment: QuickAccessEnvironment;
    onKeys: (keys: string[]) => void;
}> = ({ environment, onKeys }) => {
    const data = useLazyLoadQuery<StarredMenuQuery>(starredKeysQuery, { environment }, { fetchPolicy: 'store-and-network' });
    const serverKeys = data.quickAccessMenu?.keys ?? [];
    const signature = serverKeys.join(' ');

    useEffect(() => {
        onKeys([...serverKeys]);
    }, [signature]);

    return null;
};

export const StarredMenuProvider: FunctionComponent<PropsWithChildren<{ environment: QuickAccessEnvironment }>> = ({ environment, children }) => {
    const [keys, setKeys] = useState<string[] | null>(null);
    const [saveStarred, isSaving] = useMutation<StarredMenuSaveMutation>(saveStarredKeysMutation);

    const handleKeys = useCallback((next: string[]) => setKeys(next), []);

    const starredKeys = keys ?? [];
    const keySignature = starredKeys.join(' ');

    const value = useMemo<StarredMenuContextValue>(() => {
        const keySet = new Set(starredKeys);
        const isStarred = (item: ItemMenu) => keySet.has(getStarredKey(item)) || keySet.has(item.urlPage);
        const isStarredKey = (key: string) => keySet.has(key);

        const persist = (next: string[]) => {
            const previous = keys ?? [];
            setKeys(next);
            saveStarred({
                variables: { input: { environment, keys: next } },
                onCompleted: (response) => setKeys([...(response.saveQuickAccessMenu.keys ?? [])]),
                onError: () => setKeys(previous)
            });
        };

        const toggleStar = (item: ItemMenu) => {
            if (isSaving) return;
            const key = getStarredKey(item);
            if (isStarred(item)) persist(starredKeys.filter((currentKey) => currentKey !== key && currentKey !== item.urlPage));
            else persist([...starredKeys, key]);
        };

        const toggleStarKey = (key: string) => {
            if (isSaving || !key) return;
            if (keySet.has(key)) persist(starredKeys.filter((currentKey) => currentKey !== key));
            else persist([...starredKeys, key]);
        };

        const saveStarredKeysOrder = (nextKeys: string[]) => {
            if (isSaving || nextKeys.join(' ') === keySignature) return;
            persist(nextKeys);
        };

        return {
            isWithinProvider: true,
            starredKeys,
            isLoaded: keys !== null,
            isStarred,
            isStarredKey,
            toggleStar,
            toggleStarKey,
            saveStarredKeysOrder,
            isSaving
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keySignature, keys === null, isSaving, environment]);

    return (
        <StarredMenuContext.Provider value={value}>
            <Suspense fallback={null}>
                <StarredKeysLoader environment={environment} onKeys={handleKeys} />
            </Suspense>
            {children}
        </StarredMenuContext.Provider>
    );
};
